import { useTheme } from "@mui/material";
import * as d3 from "d3";
import { type HierarchyNode, type HierarchyRectangularNode, zoomIdentity } from "d3";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { FilePiece } from ".";
import TooltipWrapper from "./tooltipWrapper";

export type TreemapSettings = {
  width: number;
  height: number;
  margin: number;

  size: string;
  color: "category" | "provider" | "both" | "fileType" | "ext" | "nodes" | "random";
  filesize: boolean;

  groupingSize: boolean;
  private: boolean;
  tooltipWidth: number;
  showImages: boolean;
  showFolderHierarchy: boolean;
  numberOfChildren: number;
  pixelAreaSize: number;
};

const defaultSettings: TreemapSettings = {
  width: 100,
  height: 100,
  margin: 5,
  size: "uncompressed",
  color: "both",
  filesize: true,

  groupingSize: true,
  private: true,
  tooltipWidth: 100,
  showImages: true,
  showFolderHierarchy: true,
  numberOfChildren: 500,
  pixelAreaSize: 12,
};

type TreemapData = {
  name: string;
  val: number;
  data: never[];
  children: {
    name: string;
    children: FilePiece[];
  }[];
};

type ComponentProps = {
  hierarchyData: FilePiece[];
  settings: Partial<TreemapSettings>;
  fileList: FilePiece[];
};

// TODO: Refactor to somewhere else
export const getFileSize = (num: number): string => {
  if (num < 1024) return `${Math.floor(num)} Bytes`;
  num = num / 1024;
  if (num < 1024) return `${Math.floor(num)} KB`;
  num = num / 1024;
  if (num < 1024) return `${Math.floor(num)} MB`;
  num = num / 1024;
  return `${Math.floor(num)} GB`;
};

/**
 *
 * @param props
 * @returns treemap component
 */
function Treemap(props: ComponentProps) {
  const settings = { ...defaultSettings, ...props.settings };
  const theme = useTheme();
  const svgRef = useRef<SVGSVGElement | null>(null);
  const nodesGroupRef = useRef<SVGSVGElement | null>(null);
  const categories = React.useRef<{ [label: string]: string }>({});
  const [tooltip, setTooltip] = useState<{
    visible: boolean;
    files: HierarchyRectangularNode<FilePiece> | undefined;
    isFixed: boolean;
    position: { x: number; y: number };
  }>({
    isFixed: false,
    visible: false,
    files: undefined,
    position: { x: 0, y: 0 },
  });

  const [axis, setAxis] = useState<{
    x: d3.ScaleLinear<number, number>;
    y: d3.ScaleLinear<number, number>;
  }>({
    x: getScales().x,
    y: getScales().y,
  });

  const [rootPath, setRootPath] = useState<string>("");

  const createTreemap = useCallback(
    (data: d3.HierarchyNode<FilePiece>) => {
      return d3
        .treemap<FilePiece>()
        .size([settings.width, settings.height])
        .round(true)
        .padding(3)
        .paddingTop((d) => (d.data.path !== "treemap" && axis.y(d.y1) - axis.y(d.y0) > 30 ? 30 : 0))
        .tile(d3.treemapResquarify)(Object.assign(data));
    },
    [settings.width, settings.height, axis.y],
  );

  const hierarchicalTreemap = useMemo(() => {
    const data = createTree(
      props.hierarchyData
        .filter((v) => v.path.startsWith(rootPath))
        .map((v) => ({
          ...v,
          relPath: v.path.substring(rootPath.lastIndexOf("/") + 1),
        })),
    );

    return (
      <g
        onMouseLeave={() => {
          // hide tooltip when leaving the visualization
          setTooltip((prevTooltipState) => {
            if (!prevTooltipState.isFixed) {
              return {
                files: prevTooltipState.files,
                visible: false,
                isFixed: false,
                position: prevTooltipState.position,
              };
            }
            return prevTooltipState;
          });
        }}
      >
        {renderHierarchical(createTreemap(data))}
      </g>
    );
  }, [props.hierarchyData, rootPath, createTreemap]);

  useEffect(() => {
    setAxis({
      x: d3
        .scaleLinear()
        .domain([0, settings.width])
        .range([settings.margin, settings.width - settings.margin]),
      y: d3
        .scaleLinear()
        .domain([0, settings.height])
        .range([settings.margin, settings.height - settings.margin]),
    });
  }, [settings.margin, settings.width, settings.height]);

  useEffect(() => {
    const zoom = settings.showFolderHierarchy
      ? d3.zoom<SVGSVGElement, unknown>().scaleExtent([1, 1])
      : d3
          .zoom<SVGSVGElement, unknown>()
          .scaleExtent([1, 64]) // min max allowed scale factors
          //.extent([[0, 0], [this.settings.width - 50, this.settings.height - 60]])
          .extent([
            [0, 0],
            [settings.width, settings.height],
          ])
          .on("zoom", ({ transform }) => zoomed(transform));
    if (svgRef.current) {
      d3.select(svgRef.current).call(zoom, zoomIdentity);
    }
  }, [svgRef.current, settings.showFolderHierarchy, settings.width, settings.height]);

  useEffect(() => {
    zoomed(d3.zoomIdentity);
  }, [settings.showFolderHierarchy]);

  function showSize(d: HierarchyNode<FilePiece>) {
    if (d.data.isDir) return (d.children?.length ?? 0) + " Elements";
    if (!settings.filesize) return null;
    if (settings.size === "length") return d.data.datalength + " Data Points";
    return getFileSize(d.value ?? d.data.val);
  }

  function renderTitle(item: FilePiece, size: number, yOffset: number, height: number): string {
    if (!item) return "";
    if (item.sum && item.files.length > 1) return renderText(item.files.length + " Files", size, yOffset, height);
    return renderText(item.fileName, size, yOffset, height);
  }

  /**
   * Returns given string when the parent nodes height is sufficient
   * to print the text.
   * @param str Text to print
   * @param size Supposed font size
   * @param yOffset Y-Offset above the text
   * @param height Height of the parents node
   * @returns Text if the nodes height is sufficient, else empty
   */
  function renderText(str: string, size: number, yOffset: number, height: number): string {
    // Text y specifies the position of the baseline, thus a 6th is sufficient
    return yOffset + axis.y(size) / 6 > height ? "" : str;
  }

  function getColor(d: FilePiece) {
    switch (settings.color) {
      case "category":
        if (d.dataCategory === "Other") return "#fff";
        return theme.palette.categories[d.dataCategory].main;
      case "provider":
        return getRandomColorFromMap(d.providerId);
      case "both":
        return d.datalength > 0 ? theme.palette.categories[d.dataCategory].main : theme.palette.files[d.fileType].main;
      case "fileType":
        return theme.palette.files[d.fileType].main;
      case "ext":
        return getRandomColorFromMap(d.mimeType);
      case "nodes":
        return getRandomColorFromMap(d.folder);
      case "random":
      default:
        return "hsl(" + Math.random() * 360 + ",20%,50%)";
    }
  }

  function getRandomColorFromMap(d: string) {
    if (!categories.current[d]) {
      const color = "hsl(" + Math.random() * 360 + ",66%,99%)";
      categories.current[d] = color;
    }
    return categories.current[d];
  }

  /**
   * Returns nodes supposed brightness for hierarchical treemap.
   * Used in order to visualize hierarchical differences of elements with
   * the same color in the treemap.
   * @param d Node the return the brightness for
   * @returns CSS brightness value based on element's height
   */
  function applyBrightness(d: HierarchyRectangularNode<FilePiece>) {
    let base = 0;
    const isHovered = tooltip.files && tooltip.files.data.path.includes(d.data.path);
    if (isHovered) {
      base = 0.2;
    }
    const val = base + 1.1 + 0.8 / (d.depth + d.height + 2) - 1 / (d.height + 2);
    return val;
  }

  /**
   * Predict if children of elements have to be rendered.
   * @param e Element
   * @returns boolean
   */
  function checkIfVisible(e: HierarchyRectangularNode<FilePiece>) {
    return (
      // if there is a parent
      !e.parent ||
      //if x and y size is above pixelAreaSize
      (calculateHypotenuse(e.parent) > settings.pixelAreaSize &&
        // if the number of children is below numberOfChildren
        (e.parent?.children?.length ?? 0) <= settings.numberOfChildren) ||
      // or if every child is bigger then pixelAreaSize
      e.parent?.children?.every((d) => calculateHypotenuse(d) > settings.pixelAreaSize)
    );
  }

  function calculateHypotenuse(d: HierarchyRectangularNode<FilePiece>) {
    return Math.sqrt(Math.pow(axis.x(d.x1) - axis.x(d.x0), 2) * Math.pow(axis.y(d.y1) - axis.y(d.y0), 2));
  }

  /**
   * Calculate path of parent element.
   * @param p Path of element to get the parent from
   * @returns Path of parent element. May be false if p is root.
   */
  function getParentString(p: string) {
    return p.indexOf("/") > -1 && p.substring(0, p.lastIndexOf("/"));
  }

  /**
   * Zoom into element in hierarchical treemap.
   * @param d  New root node
   */
  function zoomIn(d: HierarchyRectangularNode<FilePiece>) {
    setRootPath(d.data.path);
  }

  /**
   * Zoom out of current root element in hierarchical treemap.
   * @param d Current root node
   */
  function zoomOut(d: HierarchyRectangularNode<FilePiece>) {
    const newRoot = getParentString(d.data.path);

    if (newRoot) {
      setRootPath(newRoot);
    }
  }

  function createFlatTree(d: FilePiece[]) {
    const data = d3.group(d, (d) => d.folder);
    const data2 = Array.from(data, ([name, children]) => ({ name, children }));
    const h = d3
      .hierarchy({
        name: "treemap",
        val: 0,
        data: [] as never[],
        children: data2,
      })
      .sum((d) => d.val);
    d3.treemap<TreemapData>().padding(0).size([settings.width, settings.height]).tile(d3.treemapBinary)(h);
    return h.leaves();
  }

  function createTree(arr: FilePiece[]) {
    const byId = new Map(arr.map((d) => [d.relPath, d]));

    // synthesize missing ancestor dirs
    for (const d of arr) {
      let p = d.relPath;
      while (p.includes("/")) {
        p = p.slice(0, p.lastIndexOf("/"));
        if (byId.has(p)) break;
        byId.set(p, {
          ...d,
          relPath: p,
          path: p,
          fileName: p.slice(p.lastIndexOf("/") + 1),
          isDir: true,
          val: 0,
          data: [],
          files: [],
          binaryData: undefined,
        } as FilePiece);
      }
    }

    return (
      d3
        .stratify<FilePiece>()
        .id((d) => d.relPath)
        // undefined, not "": d3 treats "" as a real id and throws "missing: "
        .parentId((d) => (d.relPath.includes("/") ? d.relPath.slice(0, d.relPath.lastIndexOf("/")) : undefined))(
          Array.from(byId.values()),
        )
        .sum((d) => d.val)
        .sort((a, b) => b.height - a.height || (b.value ?? 0) - (a.value ?? 0))
    );
  }

  function toggleTooltipFixed() {
    setTooltip((prevTooltipState) => {
      return {
        ...prevTooltipState,
        isFixed: !prevTooltipState.isFixed,
      };
    });
  }

  /* Render Treemap */
  /**
   * Render a single node in a treemap.
   * @param f Node to render
   * @param i React key value for the block
   * @returns JSX of a single svg block of a treemap
   */
  function renderBlock(f: HierarchyRectangularNode<FilePiece>) {
    const offset = 0;
    const width = Math.max(axis.x(f.x1) - axis.x(f.x0), 0);
    const height = Math.max(axis.y(f.y1) - axis.y(f.y0), 0);
    const clip = {
      clipPath: `path('M 0 ${height - offset} H ${width - offset} V 0 H 0 z')`,
    };
    const clipTitle = {
      clipPath: `path('M 0 1000 H ${
        width - (offset + (f.data.isDir && getParentString(f.data.relPath) ? 24 : 0))
      } V 0 H 0 z')`,
    };

    return (
      <g
        key={f.id}
        style={clip}
        transform={`translate(${axis.x(f.x0)}, ${axis.y(f.y0 + offset)})`}
        onClick={() => {
          if (f.data.isDir) {
            if (f.depth === 0 && getParentString(f.data.path)) zoomOut(f);
            else if (f.children) zoomIn(f);
          } else {
            toggleTooltipFixed();
          }
        }}
        onMouseMove={({ clientX, clientY }) => {
          setTooltip((prevTooltipState) => {
            if (!f.data.isDir && !prevTooltipState.isFixed) {
              return {
                files: f,
                visible: true,
                isFixed: false,
                position: { x: clientX, y: clientY },
              };
            } else {
              if (!prevTooltipState.isFixed) {
                return {
                  files: prevTooltipState.files,
                  visible: false,
                  isFixed: false,
                  position: prevTooltipState.position,
                };
              }
            }
            return prevTooltipState;
          });
        }}
      >
        {settings.showImages && f.data.binaryData ? (
          <image
            href={f.data.binaryData}
            transform={`translate(0, ${f.parent ? 0 : axis.y(20)})`}
            preserveAspectRatio={f.parent ? "xMidYMid slice" : "xMidYMid meet"}
            onClick={() => zoomIn(f)}
            width={width}
            height={height}
            cursor="pointer"
            style={{
              stroke: !settings.showFolderHierarchy ? "black" : undefined,
            }}
          ></image>
        ) : (
          <rect
            width={width}
            height={height}
            cursor={f.children && getParentString(f.data.path) ? "pointer" : "default"}
            style={{
              fill: f.data.isDir ? getFolderColor(f) : getColor(f.data),
              stroke: !settings.showFolderHierarchy ? "black" : undefined,
              filter: `brightness(${applyBrightness(f)})`,
              WebkitTransition: "-webkit-filter 200ms linear",
            }}
          ></rect>
        )}

        {f.data.isDir &&
          axis.y(f.y1) - axis.y(f.y0) > 24 &&
          axis.y(f.x1) - axis.y(f.x0) > 24 &&
          getParentString(f.data.relPath) && (
            <path
              transform={"translate(3,0)"}
              fill="#282323"
              d="M10 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2h-8l-2-2z"
            ></path>
          )}
        <text
          x={6 + (f.data.isDir && getParentString(f.data.relPath) ? 24 : 0)}
          y={20}
          cursor={f.depth === 0 && getParentString(f.data.path) ? "pointer" : "none"}
          style={clipTitle}
          fontSize={18}
          pointerEvents="none"
          opacity={0.95}
          fill={f.data.binaryData && !f.parent ? "#fff" : "#111"}
        >
          {f.depth === 0 && f.children && getParentString(f.data.path) ? "\u2039 " : ""}
          {getParentString(f.data.path) ? renderTitle(f.data, 20, 20, height) : "Files"}
        </text>
        <text
          x={6}
          y={40}
          style={clip}
          fontSize={14}
          fontWeight={100}
          pointerEvents="none"
          fillOpacity={0.8}
          fill="#222"
        >
          {f.data.binaryData !== undefined ||
            renderText(
              f.data.isDir ? (!f.children?.some((d) => checkIfVisible(d)) ? (showSize(f) ?? "") : "") : f.data.category,
              16,
              40,
              height,
            )}
        </text>
        {f.data.data.length > 0 &&
          axis.y(f.y1) - axis.y(f.y0) > 100 &&
          axis.y(f.x1) - axis.y(f.x0) > 100 &&
          f.data.data.slice(0, (axis.y(f.y1) - axis.y(f.y0)) / 15).map((d, i) => (
            <text
              overflow={"scroll"}
              key={f.data.exportId + f.data.path + i}
              x={6}
              y={56 + 32 + i * 14}
              style={clip}
              filter={settings.private ? "url(#textshadow)" : ""}
              fontSize={12}
              fontWeight={100}
              pointerEvents="none"
              //fill={settings.private ? "transparent" : "#333"}
            >
              {d.text}
            </text>
          ))}
        {f.data.isDir || f.data.binaryData || (
          <text
            x={6}
            y={56}
            style={clip}
            fontSize={12}
            fontWeight={100}
            pointerEvents="none"
            fillOpacity={0.8}
            fill="#222"
          >
            {renderText(showSize(f) ?? "", 16, 56, height)}
          </text>
        )}
      </g>
    );
  }

  /**
   * Render a given block of a hierarchical treemap and it's child
   * elements.
   * @param f Root node
   * @param i React key value of the root node
   * @returns JSX of a treemap block and it's children
   */
  function applyBlockRecursive(f: HierarchyRectangularNode<FilePiece>): React.ReactNode {
    return [renderBlock(f), f.children?.filter(checkIfVisible).map(applyBlockRecursive)];
  }

  /**
   * Render hierarchical treemap. Skip "treemap" root node.
   * @param n Treemap Root Node
   * @returns JSX of hierarchical treemap
   */
  function renderHierarchical(n: HierarchyRectangularNode<FilePiece>) {
    return [n.data.path !== "treemap" && renderBlock(n), n.children?.map(applyBlockRecursive)];
  }

  function zoomed(transform: d3.ZoomTransform) {
    // based on the current zoom we switch between transforming the
    // whole SVG or transforming every element (and hiding those not in view)
    // this makes the zoomed out and zoomed in more efficient and fluid (hopefully)
    if (transform.k > 2) {
      const { x, y } = getScales();
      setAxis({ x: transform.rescaleX(x), y: transform.rescaleY(y) });
    } else {
      if (nodesGroupRef.current) {
        if (nodesGroupRef.current.childElementCount !== props.fileList.length) {
          // rerender all nodes if zooming out
          const { x, y } = getScales();
          setAxis({ x, y });
        }
        d3.select(nodesGroupRef.current).attr("transform", String(transform));
      }
    }
  }

  function getScales() {
    return {
      x: d3
        .scaleLinear()
        .domain([0, settings.width])
        .range([settings.margin, settings.width - settings.margin]),
      y: d3
        .scaleLinear()
        .domain([0, settings.height])
        .range([settings.margin, settings.height - settings.margin]),
    };
  }

  function getFolderColor(f: HierarchyRectangularNode<FilePiece>) {
    const traverseTree = (f: HierarchyRectangularNode<FilePiece>): string | undefined | null => {
      return f.children?.reduce(
        (found, c) => {
          let val;
          if (c.data.isDir) {
            val = traverseTree(c);
          } else {
            val = getColor(c.data);
          }

          if (found === undefined || found === val) return val;
          return null;
        },
        undefined as string | undefined | null,
      );
    };

    return (
      traverseTree(f) ?? d3.scaleSequential([0, f.height + f.depth], d3.interpolate("#fffca6", "#75a5ff"))(f.height)
    );
  }

  return (
    <>
      <TooltipWrapper
        settings={settings}
        position={tooltip?.position ?? { x: 0, y: 0 }}
        files={tooltip?.files}
        isFixed={tooltip.isFixed}
        visible={tooltip.visible}
      />
      <svg ref={svgRef} width={settings.width} height={settings.height}>
        <filter id="textshadow" x="0" y="0" width="200%" height="200%">
          <feGaussianBlur stdDeviation="2"></feGaussianBlur>
        </filter>
        <g>
          <g ref={nodesGroupRef}>
            {settings.showFolderHierarchy
              ? props.hierarchyData && hierarchicalTreemap
              : createFlatTree(props.fileList).map((d) =>
                  renderBlock(d as unknown as HierarchyRectangularNode<FilePiece>),
                )}
          </g>
        </g>
      </svg>
    </>
  );
}

export default Treemap;
